import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Button, Grid, Stack, Box, CircularProgress } from "@mui/material";
import { Autocomplete, InputLabel, TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import InfoIcon from "@mui/icons-material/Info";
import debounce from "lodash.debounce";
import ApiClient from "helpers/ApiClient";
import { validationSchema } from "./validation";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import vat from "../../../../../../../assets/signup/vat.png";
import post from "../../../../../../../assets/signup/post.png";
import number from "../../../../../../../assets/signup/number.png";
import companyIcon from "../../../../../../../assets/signup/building.png";
import EmployeeVerification from "../employeeVerification/employeVerification";
import "./companyVerification.scss";
import { Search } from "@mui/icons-material";
import { PatternFormat } from "react-number-format";

interface CompanyDetails {
  name: string | string[];
  regNo: string | string[];
  postCode: string | string[];
}

const CompanyVerification = ({ setToggleSnackbar, userId }: any) => {
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showEmpVerif, setShowEmpVerif] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>({
    name: "",
    regNo: "",
    postCode: "",
  });

  //Formik function
  const initialValues = {
    company: companyDetails.name || "",
    companyNumber: companyDetails.regNo || "",
    adminEmail: "",
    adminNumber: "",
    vatNumber: "",
    postCode: companyDetails.postCode || "",
  };

  const { values, errors, touched, handleChange, handleSubmit, handleBlur } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => {
      createCompany(values);
    },
  });

  //Searching for the companies
  const searchCompany = async () => {
    if (values.company.length > 1) {
      try {
        const res = await ApiClient.post(`company/search?name=${query}`);
        const companies = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCompanies(companies);

        //Find selected company
        const selectedCompany = companies.find((c: any) => c["company_name"] === query);
        setSelectedCompany(selectedCompany ?? null);
        if (selectedCompany) {
          setCompanyDetails({
            name: selectedCompany["company_name"],
            regNo: selectedCompany["company_number"],
            postCode: selectedCompany["registered_office_address"]?.["postal_code"] ?? "",
          });
        }
      } catch (err: any) {
        const error = err?.response?.data?.message;
        setToggleSnackbar({ message: error, isToggle: true, mode: "error", top: "110px" });
      }
    }
  };
  const debouncedResults = useMemo(() => {
    return debounce(searchCompany, 500);
  }, []);

  //New Company Creation
  const createCompany = (values: any) => {
    setIsLoading(true);
    const { adminEmail, adminNumber, vatNumber, postCode, company, companyNumber } = values;
    // API expects companyDetails as an object; use selectedCompany from search or build from form
    const hasSelectedCompany =
      selectedCompany &&
      typeof selectedCompany === "object" &&
      Object.keys(selectedCompany).length > 0;
    const companyDetailsPayload = hasSelectedCompany
      ? selectedCompany
      : {
          company_name: company,
          company_number: companyNumber,
          registered_office_address: { postal_code: postCode || "" },
        };

    // Backend expects companyDetails as a JSON string and parses it; sending an object causes "[object Object]" parse error
    const payload = {
      userId,
      name: company,
      registrationNumber: companyNumber,
      adminEmail,
      adminPhone: adminNumber,
      vatNumber: +vatNumber,
      postCode,
      companyDetails: JSON.stringify(companyDetailsPayload),
    };

    ApiClient.post("company/add", payload)
      .then((res) => {
        setCompanyId(res.data.data.id);
        setIsLoading(false);
        setShowEmpVerif(true);
      })
      .catch((err: any) => {
        const error = err.response.data.message;
        setIsLoading(false);
        setToggleSnackbar({ message: error, isToggle: true, mode: "error", top: "110px" });
      });
  };

  useEffect(() => {
    searchCompany();
    return () => {
      debouncedResults.cancel();
    };
  }, [query]);

  return (
    <>
      {!showEmpVerif && (
        <Box className="identity-verification">
          <form onSubmit={handleSubmit}>
            <Typography variant="h5" sx={{ mb: "20px", color: "#434242", fontWeight: "500" }}>
              Company Verfication
            </Typography>
            <Grid container spacing={2} rowSpacing={3}>
              <Grid item md={12} sm={12} xs={12}>
                <InputLabel
                  htmlFor="company"
                  className={errors.company && touched.company ? "label-err" : "label"}
                >
                  Company <span style={{color:'red'}}>*</span>
                </InputLabel>

                <Autocomplete
                  id="company"
                  freeSolo
                  options={[]}
                  // options={companies?.map((c: any) => c["company_name"])}
                  onInputChange={(event, value) => {
                    setQuery(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="company"
                      sx={{ background: "#fff" }}
                      fullWidth
                      type="text"
                      size="small"
                      variant="outlined"
                      error={!!(errors.company && touched.company)}
                      value={values.company}
                      onChange={(e) => {
                        handleChange(e);
                        debouncedResults();
                      }}
                      onBlur={handleBlur}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end" sx={{ paddingRight: "12px" }}>
                            {errors.company && touched.company ? (
                              <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                            ) : (
                              <img src={companyIcon} alt="icon" />
                            )}
                          </InputAdornment>
                        ),
                        startAdornment:(
                          <InputAdornment position="start" sx={{ paddingLeft: "8px" }}>
                             <Search sx={{paddingLeft:'8px'}}/>
                            </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
                {errors.company && touched.company && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors.company}
                  </Typography>
                )}
              </Grid>

              <Grid item md={12} sm={12} xs={12}>
                <InputLabel
                  htmlFor="companyNumber"
                  className={errors.companyNumber && touched.companyNumber ? "label-err" : "label"}
                >
                  Company Registration Number <span style={{color:'red'}}>*</span>
                </InputLabel>

                <TextField
                  name="companyNumber"
                  sx={{ background: "#fff" }}
                  fullWidth
                  type="text"
                  size="small"
                  variant="outlined"
                  error={!!(errors.companyNumber && touched.companyNumber)}
                  value={values.companyNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {errors.companyNumber && touched.companyNumber ? (
                          <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                        ) : (
                          <img src={number} alt="icon" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {errors.companyNumber && touched.companyNumber && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors?.companyNumber}
                  </Typography>
                )}
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <InputLabel
                  htmlFor="adminEmail"
                  className={errors.adminEmail && touched.adminEmail ? "label-err" : "label"}
                >
                  Admin Email <span style={{color:'red'}}>*</span>
                </InputLabel>

                <TextField
                  name="adminEmail"
                  sx={{ background: "#fff" }}
                  fullWidth
                  type="email"
                  size="small"
                  variant="outlined"
                  error={!!(errors.adminEmail && touched.adminEmail)}
                  value={values.adminEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {errors.adminEmail && touched.adminEmail ? (
                          <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                        ) : (
                          <EmailIcon fontSize="medium" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {errors.adminEmail && touched.adminEmail && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors.adminEmail}
                  </Typography>
                )}
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <InputLabel
                  htmlFor="adminNumber"
                  className={errors.adminNumber && touched.adminNumber ? "label-err" : "label"}
                >
                  Admin Phone Number <span style={{color:'red'}}>*</span>
                </InputLabel>
                <PatternFormat
                displayType="input"
                format="+## ###### ####"
                customInput={TextField}
                name="adminNumber"
                type="text"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="+44 000000 0000"
                error={!!(errors.adminNumber && touched.adminNumber)}
                value={values.adminNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.adminNumber && touched.adminNumber ? (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <LocalPhoneIcon fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

                {errors.adminNumber && touched.adminNumber && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors.adminNumber}
                  </Typography>
                )}
              </Grid>
              <Grid item md={6} xs={12}>
                <InputLabel
                  htmlFor="vatNumber"
                  className={errors.vatNumber && touched.vatNumber ? "label-err" : "label"}
                >
                  VAT Number <span style={{color:'red'}}>*</span>
                </InputLabel>

                <TextField
                  name="vatNumber"
                  sx={{ background: "#fff" }}
                  fullWidth
                  type="text"
                  size="small"
                  variant="outlined"
                  error={!!(errors.vatNumber && touched.vatNumber)}
                  value={values.vatNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {errors.vatNumber && touched.vatNumber ? (
                          <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                        ) : (
                          <img src={vat} alt="icon" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {errors.vatNumber && touched.vatNumber && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors.vatNumber}
                  </Typography>
                )}
              </Grid>
              <Grid item md={6} xs={12}>
                <InputLabel
                  htmlFor="postCode"
                  className={errors.postCode && touched.postCode ? "label-err" : "label"}
                >
                  Post Code <span style={{color:'red'}}>*</span>
                </InputLabel>

                <TextField
                  name="postCode"
                  sx={{ background: "#fff" }}
                  fullWidth
                  type="text"
                  size="small"
                  variant="outlined"
                  error={!!(errors.postCode && touched.postCode)}
                  value={values.postCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {errors.postCode && touched.postCode ? (
                          <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                        ) : (
                          <img src={post} alt="icon" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {errors.postCode && touched.postCode && (
                  <Typography sx={{ color: "#DC3545" }} variant="body2">
                    {errors?.postCode}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Stack
              spacing={12}
              direction="row"
              justifyContent="flex-end"
              sx={{ textAlign: "center", mt: 4 }}
            >
              <Button
                className="btn-primary"
                sx={{ width: { xs: "100%", md: "auto" } }}
                type="submit"
                disableRipple
                variant="contained"
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: "#FFFFFF", mr: 1 }} />
                    <span> Loading...</span>
                  </>
                ) : (
                  <span>Continue</span>
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      )}
      {showEmpVerif && (
        <EmployeeVerification setToggleSnackbar={setToggleSnackbar} companyId={companyId} />
      )}
    </>
  );
};
export default CompanyVerification;
